// Builds the standalone infoscreen HTML document for the editor preview.
// This mirrors buildInfoscreenHTML in display-client/src/pages/Player.tsx so
// the preview matches exactly what a real display renders. Keep the two in sync.

function substituteVariables(str: string, map: Record<string, string>): string {
  if (!str) return '';
  let out = str;
  for (let pass = 0; pass < 4; pass++) {
    let changed = false;
    out = out.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (m, key) => {
      if (Object.prototype.hasOwnProperty.call(map, key)) {
        changed = true;
        return map[key];
      }
      return m;
    });
    if (!changed) break;
  }
  return out;
}

export function buildInfoscreenDoc(config: any, apiBase: string, baseW: number, baseH: number): string {
  const feedUrl = config.feedUrl || '';
  const feedMin = config.feedItemsMin || 3;
  const feedMax = config.feedItemsMax || 5;
  const tickerMax = config.tickerMax || 5;
  const infoscreenId = config._id;
  const openingHours = config.openingHours || [];
  const openingHoursJson = JSON.stringify(openingHours).replace(/</g, '\\u003c').replace(/'/g, '\\u0027');

  const apiOrigin = apiBase.replace(/\/api\/?$/, '');
  const assetBase = apiOrigin + '/uploads/infoscreen';
  const variableMap: Record<string, string> = {};
  (config.variables || []).forEach((v: any) => {
    if (v && v.key) variableMap[v.key] = v.value != null ? String(v.value) : '';
  });
  variableMap.API_BASE = apiBase;
  variableMap.ASSET_BASE = assetBase;
  variableMap.INFOSCREEN_ID = infoscreenId;
  variableMap.BASE_W = String(baseW);
  variableMap.BASE_H = String(baseH);
  if (variableMap.FEED_URL === undefined) variableMap.FEED_URL = feedUrl;

  const customCss = substituteVariables(config.customCss || '', variableMap);
  const customHtml = substituteVariables(
    config.customHtml ||
      `
<div id="scale-wrapper">
  <div id="header"><h1>${config.name || 'Infoscreen'}</h1></div>
  <div id="main-content">
    <div id="live-ticker"></div>
    <div id="opening-hours"></div>
    <div id="feed-list"></div>
  </div>
</div>`,
    variableMap
  );
  const hasCustomJs = !!(config.customJs && config.customJs.trim());
  const customJs = hasCustomJs ? substituteVariables(config.customJs, variableMap) : '';

  const genericScript = `
    const BASE_W = ${baseW};
    const BASE_H = ${baseH};
    const API_BASE = '${apiBase}';
    const INFOSCREEN_ID = '${infoscreenId}';
    const FEED_URL = '${feedUrl}';
    const FEED_MIN = ${feedMin};
    const FEED_MAX = ${feedMax};
    const TICKER_MAX = ${tickerMax};
    const TICKER_RELOAD = 30000;
    const FEED_RELOAD = 120000;
    const OPENING_HOURS = JSON.parse('${openingHoursJson}');
    const DAY_NAMES = ['Montag','Dienstag','Mittwoch','Donnerstag','Freitag','Samstag','Sonntag'];

    function scaleToFit() {
      const wrapper = document.getElementById('scale-wrapper');
      if (!wrapper) return;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const scale = Math.min(vw / BASE_W, vh / BASE_H);
      wrapper.style.transform = 'translate(-50%, -50%) scale(' + scale + ')';
    }
    scaleToFit();
    window.addEventListener('resize', scaleToFit);

    async function loadTickers() {
      try {
        const res = await fetch(API_BASE + '/infoscreens/' + INFOSCREEN_ID + '/tickers');
        const data = await res.json();
        const tickers = (data.data || []).slice(0, TICKER_MAX);
        const el = document.getElementById('live-ticker');
        if (!el) return;
        if (tickers.length === 0) {
          el.innerHTML = '<div class="no-ticker">Keine aktuellen Meldungen</div>';
          return;
        }
        el.innerHTML = tickers.map(function(t) {
          return '<div class="ticker-item ' + (t.type || 'info') + '">' +
            '<div class="ticker-text">' + t.text + '</div>' +
            (t.subtext ? '<div class="ticker-subtext">' + t.subtext + '</div>' : '') +
            '</div>';
        }).join('');
      } catch (e) { console.error('Ticker load error', e); }
    }

    async function loadFeed() {
      if (!FEED_URL) return;
      try {
        const proxyUrl = API_BASE + '/infoscreens/proxy-feed?url=' + encodeURIComponent(FEED_URL);
        let items = [];
        try {
          const res = await fetch(proxyUrl);
          const data = await res.json();
          items = data.items || [];
        } catch(e) {
          const res = await fetch(FEED_URL);
          const text = await res.text();
          const parser = new DOMParser();
          const xml = parser.parseFromString(text, 'text/xml');
          xml.querySelectorAll('item, entry').forEach(function(entry) {
            items.push({
              title: (entry.querySelector('title') || {}).textContent || '',
              description: (entry.querySelector('description, summary, content') || {}).textContent || '',
              date: (entry.querySelector('pubDate, published, updated') || {}).textContent || ''
            });
          });
        }
        const count = Math.min(Math.max(items.length, FEED_MIN), FEED_MAX);
        items = items.slice(0, count);
        const el = document.getElementById('feed-list');
        if (!el) return;
        if (items.length === 0) { el.innerHTML = ''; return; }
        el.innerHTML = items.map(function(item) {
          var d = item.date ? new Date(item.date).toLocaleDateString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric'}) : '';
          var desc = (item.description || '').replace(/<[^>]+>/g, '').substring(0, 200);
          return '<div class="feed-item">' +
            '<div class="feed-left">' +
            (d ? '<div class="feed-meta">' + d + '</div>' : '') +
            '<div class="feed-title">' + item.title + '</div>' +
            (desc ? '<div class="feed-sub">' + desc + '</div>' : '') +
            '</div>' +
            '</div>';
        }).join('');
      } catch (e) { console.error('Feed load error', e); }
    }

    function loadOpeningHours() {
      if (!OPENING_HOURS || OPENING_HOURS.length === 0) return;
      var now = new Date();
      var jsDay = now.getDay();
      var dayIndex = (jsDay + 6) % 7;
      var hhmm = ('0' + now.getHours()).slice(-2) + ':' + ('0' + now.getMinutes()).slice(-2);

      OPENING_HOURS.forEach(function(section) {
        var containerId = 'list-' + section.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        var statusId = 'status-' + section.name.toLowerCase().replace(/[^a-z0-9]/g, '');
        var listEl = document.getElementById(containerId);
        var statusEl = document.getElementById(statusId);
        var slots = (section.slots || []).sort(function(a,b){ return a.day - b.day; });

        if (statusEl) {
          var todaySlot = slots.find(function(s){ return s.day === dayIndex; });
          if (todaySlot && !todaySlot.closed && hhmm >= todaySlot.open && hhmm <= todaySlot.close) {
            statusEl.textContent = 'Geöffnet';
            statusEl.style.background = 'rgba(34,197,94,0.15)';
            statusEl.style.color = '#22c55e';
          } else {
            statusEl.textContent = 'Geschlossen';
            statusEl.style.background = 'rgba(239,68,68,0.1)';
            statusEl.style.color = '#ef4444';
          }
        }

        if (listEl) {
          listEl.innerHTML = slots.map(function(sl) {
            var isToday = sl.day === dayIndex;
            return '<div class="agenda-row' + (isToday ? ' today' : '') + '">' +
              '<span class="time">' + DAY_NAMES[sl.day] + '</span>' +
              '<span class="desc">' + (sl.closed ? 'Geschlossen' : sl.open + ' – ' + sl.close) + '</span>' +
              '</div>';
          }).join('');
        }
      });

      var genericEl = document.getElementById('opening-hours');
      if (genericEl && genericEl.innerHTML.trim() === '') {
        genericEl.innerHTML = OPENING_HOURS.map(function(section) {
          var slots = (section.slots || []).sort(function(a,b){ return a.day - b.day; });
          var todaySlot = slots.find(function(s){ return s.day === dayIndex; });
          var isOpen = todaySlot && !todaySlot.closed && hhmm >= todaySlot.open && hhmm <= todaySlot.close;
          return '<div class="oeffnung-card">' +
            '<div class="oeffnung-card-title">' + section.name + '</div>' +
            '<div class="status-badge" style="background:' + (isOpen ? 'rgba(34,197,94,0.15);color:#22c55e' : 'rgba(239,68,68,0.1);color:#ef4444') + '">' +
              (isOpen ? 'Geöffnet' : 'Geschlossen') + '</div>' +
            slots.map(function(sl) {
              var isToday = sl.day === dayIndex;
              return '<div class="agenda-row' + (isToday ? ' today' : '') + '">' +
                '<span class="time">' + DAY_NAMES[sl.day] + '</span>' +
                '<span class="desc">' + (sl.closed ? 'Geschlossen' : sl.open + ' – ' + sl.close) + '</span>' +
                '</div>';
            }).join('') +
            '</div>';
        }).join('');
      }
    }

    loadOpeningHours();
    loadTickers();
    loadFeed();
    setInterval(loadTickers, TICKER_RELOAD);
    setInterval(loadFeed, FEED_RELOAD);
  `;

  const script = hasCustomJs ? customJs : genericScript;

  const head = '<!DOCTYPE html><html lang="de"><head><meta charset="utf-8">'
    + '<meta name="viewport" content="width=' + baseW + '">'
    + '<meta name="google" content="notranslate">'
    + '<style>' + customCss + '</style></head>';
  const body = '<body>' + customHtml + '<script>' + script + '<\/script></body></html>';
  return head + body;
}
